import type {
  BlockSwitchIssueMap,
  SwitchValidationResult,
  SwitchValidationRow
} from '@/components/building-block-form/utils/switchValidation'
import { ISSUE_SEVERITIES, type IssueSeverity } from '@/constants/issueSeverity'

export type AffectedBuildingBlockSummary = {
  blockId: number
  blockName: string
  severity: IssueSeverity
  materialNames: string[]
}

type GroupedBlockRow = { blockId: number; blockName: string; materialNames: string[] }

export const buildAffectedBuildingBlocks = (
  issueMap: BlockSwitchIssueMap
): AffectedBuildingBlockSummary[] => {
  return Object.entries(issueMap)
    .map(([rawBlockId, issue]) => {
      const blockId = Number(rawBlockId)
      const severity: IssueSeverity =
        issue.missingRows.length > 0 ? ISSUE_SEVERITIES.MISSING : ISSUE_SEVERITIES.CHANGED
      const sourceRows = issue.missingRows.length ? issue.missingRows : issue.changedRows
      const blockName = sourceRows[0]?.block_name || `Block ${blockId}`
      const materialNameSet = new Set<string>()

      for (const row of [...issue.missingRows, ...issue.changedRows]) {
        const materialName = row.product_name?.trim()
        if (materialName) materialNameSet.add(materialName)
      }

      return {
        blockId,
        blockName,
        severity,
        materialNames: Array.from(materialNameSet)
      }
    })
    .sort((left, right) => left.blockName.localeCompare(right.blockName))
}

export const groupRowsByBlock = (
  rows: Array<{ block_id: number; block_name: string; product_name: string }>
) => {
  const grouped: Record<number, GroupedBlockRow> = {}

  for (const row of rows) {
    const blockId = Number(row.block_id)
    const existing = grouped[blockId]
    if (!existing) {
      grouped[blockId] = {
        blockId,
        blockName: row.block_name || `Block ${blockId}`,
        materialNames: row.product_name ? [row.product_name] : []
      }
      continue
    }

    if (row.product_name && !existing.materialNames.includes(row.product_name)) {
      existing.materialNames.push(row.product_name)
    }
  }

  return Object.values(grouped).sort((left, right) =>
    left.blockName.localeCompare(right.blockName)
  )
}

export const dedupeRowsByMaterialAndBlock = <
  TRow extends {
    material_id: number
    block_id: number
  }
>(
  rows: TRow[]
) => {
  const seen = new Set<string>()
  const deduped: TRow[] = []

  for (const row of rows) {
    const key = `${Number(row.material_id)}-${Number(row.block_id)}`
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(row)
  }

  return deduped
}

export const collectSelectedBuildingBlockIds = (
  blocks: Array<{ id: number; calculation: number; selected: boolean }>,
  calculationId: number
): Set<number> => {
  const selectedBlockIds = new Set<number>()
  if (!calculationId) return selectedBlockIds

  for (const block of blocks) {
    if (block.calculation !== calculationId || !block.selected) continue
    selectedBlockIds.add(Number(block.id))
  }

  return selectedBlockIds
}

export const filterRowsByAllowedBlockIds = <TRow extends { block_id: number }>(
  rows: TRow[],
  allowedBlockIds: Set<number>
): TRow[] => {
  return rows.filter((row) => allowedBlockIds.has(Number(row.block_id)))
}

export const collectTransportConfigBuildingBlockIds = (
  configs: Array<{
    calculation: number
    apply_to_all_building_block: boolean
    building_block: number | null
  }>,
  calculationId: number
): Set<number> => {
  const transportConfigBlockIds = new Set<number>()

  for (const config of configs) {
    if (
      Number(config.calculation) !== calculationId ||
      config.apply_to_all_building_block ||
      config.building_block == null
    ) {
      continue
    }

    const buildingBlockId = Number(config.building_block)
    if (buildingBlockId > 0) {
      transportConfigBlockIds.add(buildingBlockId)
    }
  }

  return transportConfigBlockIds
}

export const buildSelectionSwitchNoticeData = (payload: {
  validationResult: SwitchValidationResult
  selectedBlockIds: Set<number>
  designLinkMissingRows: SwitchValidationRow[]
  designLinkChangedRows: SwitchValidationRow[]
}) => {
  const selectionMissingRows = dedupeRowsByMaterialAndBlock([
    ...(payload.validationResult?.selection_missing_rows ?? []),
    ...filterRowsByAllowedBlockIds(payload.designLinkMissingRows, payload.selectedBlockIds)
  ])

  const selectionChangedRows = dedupeRowsByMaterialAndBlock([
    ...(payload.validationResult?.selection_changed_rows ?? []),
    ...filterRowsByAllowedBlockIds(payload.designLinkChangedRows, payload.selectedBlockIds)
  ])

  return {
    hasIssues: selectionMissingRows.length > 0 || selectionChangedRows.length > 0,
    missingRows: selectionMissingRows,
    changedRows: selectionChangedRows,
    missingBlocks: groupRowsByBlock(selectionMissingRows),
    changedBlocks: groupRowsByBlock(selectionChangedRows)
  }
}

export const buildTransportConfigMissingRows = (payload: {
  validationResult: SwitchValidationResult
  designLinkMissingRows: SwitchValidationRow[]
  transportConfigBlockIds: Set<number>
}): SwitchValidationRow[] => {
  return dedupeRowsByMaterialAndBlock([
    ...payload.designLinkMissingRows,
    ...(payload.validationResult?.selection_missing_rows ?? [])
  ]).filter((row) => payload.transportConfigBlockIds.has(Number(row.block_id)))
}
